/**
 * ZeroEffortMigration — one-page marketing promise for landlords currently
 * on Buildium / AppFolio / TenantCloud who want to switch to Central Rentals.
 * Route: /migration (canonical) + legacy alias /zero-effort-migration.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Upload, CheckCircle2, Phone } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

const STEPS = [
  { title: "Export your data — we&apos;ll handle the rest",
    body: "You export CSVs from your current tool (Buildium, AppFolio, TenantCloud, or even Google Sheets). Zero mapping work — we&apos;ll normalise fields, addresses, and tenant records for you." },
  { title: "45-minute onboarding call",
    body: "A senior team member runs you through your new dashboard live. We migrate your first 3 leases and connect rent collection while you watch." },
  { title: "Verify before we go live",
    body: "You get a preview environment first. Nothing goes live for your tenants until you say the totals, lease dates, and portal invites look correct." },
];

const GUARANTEE_ITEMS = [
  "We do the CSV mapping — you don&apos;t touch a spreadsheet",
  "We migrate up to 20 properties + tenants for free on any paid plan",
  "If we can&apos;t finish migration in 5 business days, your first month is on us",
  "If a lease record moves incorrectly, we fix it inside 24 hours",
];

export default function ZeroEffortMigration() {
  usePageTitle("Zero-Effort Migration Guarantee — Central Rentals Canada");
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="migration-page">
      <PublicHeader light />
      <section className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
            <Shield className="w-3.5 h-3.5"/> Zero-Effort Migration Guarantee
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Switch to Central Rentals without a single spreadsheet.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            We&apos;ve moved landlords off Buildium, AppFolio, TenantCloud, and DIY Google Sheets. Every migration is done by a real person on our team — you spend at most 45 minutes on a call.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/signup?role=landlord&plan=starter" data-testid="migration-cta-start"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-base font-extrabold shadow-lg shadow-emerald-600/20">
              Start my migration <ArrowRight className="w-5 h-5"/>
            </Link>
            <a href="https://calendly.com/central-rentals-canada" target="_blank" rel="noopener noreferrer" data-testid="migration-cta-call"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-slate-200 hover:border-emerald-400 text-slate-800 text-base font-extrabold">
              <Phone className="w-4 h-4"/> Book a 15-min call
            </a>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} data-testid={`migration-step-${i}`} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-extrabold text-lg">{i + 1}</div>
              <h3 className="mt-4 font-display text-lg font-extrabold text-slate-900" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.body }} />
            </div>
          ))}
        </div>

        <div className="mt-14 max-w-3xl mx-auto bg-slate-900 text-white rounded-3xl p-8 md:p-10" data-testid="migration-guarantee">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Our promise, in writing</div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">The Central Rentals guarantee</h2>
          <ul className="mt-6 space-y-3">
            {GUARANTEE_ITEMS.map((g, i) => (
              <li key={i} className="flex items-start gap-3 text-base leading-relaxed" data-testid={`migration-guarantee-item-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-none mt-0.5"/>
                <span dangerouslySetInnerHTML={{ __html: g }} />
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup?role=landlord&plan=starter" data-testid="migration-guarantee-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-base font-extrabold">
              Claim my migration <ArrowRight className="w-5 h-5"/>
            </Link>
          </div>
        </div>

        <div className="mt-14 max-w-2xl mx-auto">
          <h2 className="font-display text-2xl font-extrabold">What we&apos;ll need from you</h2>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><Upload className="w-4 h-4 mt-0.5 text-emerald-600 flex-none"/> One CSV export from your current tool (Properties, Units, Tenants, Leases).</li>
            <li className="flex items-start gap-2"><Upload className="w-4 h-4 mt-0.5 text-emerald-600 flex-none"/> Your bank/Stripe account for rent collection routing.</li>
            <li className="flex items-start gap-2"><Upload className="w-4 h-4 mt-0.5 text-emerald-600 flex-none"/> A 45-minute call slot — book it above.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
